import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeEvent, type UploadPolicyDto } from '@fas/types';
import { api, type QueryParams } from '@/lib/api';
import { useRealtimeRefresh } from '@/lib/realtime';
import type { RequestKind } from './labels';

/*
 * Query keys: every request query starts with '/requests' and every inbox query with
 * '/approvals', so one realtime hint or mutation refreshes all affected views.
 */

export function useRequestPage<T>(kind: RequestKind, path: string, params: QueryParams, enabled = true) {
  return useQuery({
    queryKey: ['/requests', kind, 'list', params],
    queryFn: ({ signal }) => api.getPage<T>(path, params, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRequestDetail<T>(kind: RequestKind, path: string) {
  return useQuery({
    queryKey: ['/requests', kind, 'detail', path],
    queryFn: ({ signal }) => api.get<T>(path, undefined, signal),
  });
}

/** Invalidates everything a request decision can change. */
export function useRefreshAfterRequestChange() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/requests'] }),
      queryClient.invalidateQueries({ queryKey: ['/approvals'] }),
      queryClient.invalidateQueries({ queryKey: ['/attendance'] }),
      queryClient.invalidateQueries({ queryKey: ['/schedules'] }),
      queryClient.invalidateQueries({ queryKey: ['/exceptions'] }),
      // Leave requests change balances and the leave calendar (Phase 2F).
      queryClient.invalidateQueries({ queryKey: ['/leave-balances'] }),
      queryClient.invalidateQueries({ queryKey: ['/leave-calendar'] }),
    ]);
  };
}

/**
 * Refetches request, inbox and exception-overview queries when the server announces an
 * approval change (at most once per `delayMs`). Mounted once in the application shell.
 */
export function useApprovalLiveRefresh(delayMs = 1_000): void {
  const queryClient = useQueryClient();
  useRealtimeRefresh(
    RealtimeEvent.APPROVAL_UPDATED,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['/requests'] });
      void queryClient.invalidateQueries({ queryKey: ['/approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['/attendance', 'exceptions-dashboard'] });
    },
    delayMs,
  );
}

/** Number of requests waiting for the caller's decision (navigation badge). */
export function useApprovalCount(enabled = true) {
  return useQuery({
    queryKey: ['/approvals', 'count'],
    queryFn: ({ signal }) => api.get<{ pending: number; byWorkflow: Record<string, number> }>('/approvals/count', undefined, signal),
    refetchInterval: 120_000,
    enabled,
  });
}

/** Accepted file formats and limits (§70 "Uploads"). */
export function useUploadPolicy() {
  return useQuery({
    queryKey: ['/uploads/policy'],
    queryFn: ({ signal }) => api.get<UploadPolicyDto>('/uploads/policy', undefined, signal),
    staleTime: 10 * 60_000,
  });
}
